import { ref, reactive } from '@vue/composition-api'
import biosecurityApi from '@/services/api/biosecurity'
import useScrollToError from '@/composables/useScrollToError'
import EventBus from '@/store/EventBus'

export default function useBiosecurityForm(draftId) {
  const isEdit = ref(false)
  const loading = ref(false)
  const submitting = ref(false)
  const errors = ref({})
  useScrollToError(errors)

  const form = reactive({
    namePersonInCharge: '',
    address: '',
    phone: '',
    email: '',
    propertyOfOrigin: '',
    destination: '',
    namePersonReceiving: '',
    pic: '',
    movementType: null,
    reasonNoTicks: '',
    signatureName: '',
    date: '',
    terms: false,
    horses: [
      { _key: 1, registeredName: '', breed: '', sex: '', identificationMarks: '', pic: '' }
    ]
  })

  async function fetchForm() {
    loading.value = true
    try {
      const res = await biosecurityApi.getForm(draftId)
      const data = res.data.data
      if (data.exists && data.biosecurity) {
        isEdit.value = true
        const b = data.biosecurity
        form.namePersonInCharge = b.name_person_in_charge != null ? b.name_person_in_charge : ''
        form.address = b.address != null ? b.address : ''
        form.phone = b.phone != null ? b.phone : ''
        form.email = b.email != null ? b.email : ''
        form.propertyOfOrigin = b.property_of_origin != null ? b.property_of_origin : ''
        form.destination = b.destination != null ? b.destination : ''
        form.namePersonReceiving = b.name_person_receiving != null ? b.name_person_receiving : ''
        form.pic = b.pic != null ? b.pic : ''
        form.movementType = b.movement_type
        form.reasonNoTicks = b.reason_no_ticks != null ? b.reason_no_ticks : ''
        form.signatureName = b.name != null ? b.name : ''
        form.date = b.date != null ? b.date : ''
        if (b.horses_data != null && Array.isArray(b.horses_data)) {
          form.horses = b.horses_data.map((h, i) => ({
            _key: i + 1,
            registeredName: h.registered_name != null ? h.registered_name : '',
            breed: h.breed != null ? h.breed : '',
            sex: h.sex != null ? h.sex : '',
            identificationMarks: h.identification_marks != null ? h.identification_marks : '',
            pic: h.pic != null ? h.pic : ''
          }))
        }
      }
    } catch (_) {
      isEdit.value = false
    } finally {
      loading.value = false
    }
  }

  function buildPayload() {
    return {
      name_person_in_charge: form.namePersonInCharge,
      address: form.address,
      phone: form.phone,
      email: form.email,
      property_of_origin: form.propertyOfOrigin,
      destination: form.destination,
      name_person_receiving: form.namePersonReceiving,
      pic: form.pic,
      movement_type: form.movementType,
      reason_no_ticks: form.reasonNoTicks,
      name: form.signatureName,
      date: form.date,
      terms: form.terms,
      horses_data: form.horses.map(h => ({
        registered_name: h.registeredName,
        breed: h.breed,
        sex: h.sex,
        identification_marks: h.identificationMarks,
        pic: h.pic
      }))
    }
  }

  function validate() {
    const e = {}
    function req(formKey, apiKey, label) {
      const val = form[formKey]
      if (val == null || val === '') e[apiKey] = [`The ${label} field is required.`]
    }
    req('phone', 'phone', 'phone number')
    req('email', 'email', 'email')
    req('propertyOfOrigin', 'property_of_origin', 'property of origin')
    req('destination', 'destination', 'destination')
    req('pic', 'pic', 'PIC number')
    if (form.movementType == null) e.movement_type = ['The movement type field is required.']
    if (form.horses.length === 0 || form.horses.every(h => !h.registeredName)) {
      e.horses_data = ['At least one horse with a registered name is required.']
    }
    req('signatureName', 'name', 'name')
    req('date', 'date', 'date')
    if (!form.terms) e.terms = ['You must agree to the terms.']
    return e
  }

  async function submitForm() {
    errors.value = {}
    const clientErrors = validate()
    if (Object.keys(clientErrors).length > 0) {
      errors.value = clientErrors
      EventBus.$emit('http-error', 'Please fill in all required fields.')
      return false
    }
    submitting.value = true
    try {
      const payload = buildPayload()
      const res = isEdit.value
        ? await biosecurityApi.updateForm(draftId, payload)
        : await biosecurityApi.createForm(draftId, payload)
      if (res.data.message) EventBus.$emit('show-success', res.data.message)
      return true
    } catch (err) {
      const data = err.response != null ? err.response.data : null
      if (data != null && data.code === 'validation_error') {
        errors.value = data.errors
      }
      const message = data != null && data.message
        ? data.message
        : 'A server error occurred. Please try again later.'
      EventBus.$emit('http-error', message)
      return false
    } finally {
      submitting.value = false
    }
  }

  async function downloadBlankForm() {
    try {
      const res = await biosecurityApi.getBlankPdf()
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      window.open(blobUrl, '_blank')
    } catch (_) { /* http.js handles toast */ }
  }

  return {
    isEdit,
    loading,
    submitting,
    form,
    errors,
    fetchForm,
    submitForm,
    downloadBlankForm
  }
}



// WEBPACK FOOTER //
// ./src/composables/biosecurity/useBiosecurityForm.js